import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createIntegration, updateIntegration } from 'api/integrations';
import * as IntegrationItem from 'assets/js/pages/account-settings/account-integrations/IntegrationItem';
import * as Form from 'components/form';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface DonorSearchFormValues {
    apiKey: string;
}

const DonorSearch = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data } = useCurrentAccountIntegrations();
    const { donorSearch } = data || {};
    const isEnabled = !!donorSearch?.active;

    const methods = Form.useGivebutterForm<DonorSearchFormValues>({
        defaultValues: { apiKey: donorSearch?.authentication.key || '' },
    });
    const { control, handleSubmit, path } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        DonorSearchFormValues
    >({
        mutationFn: values => {
            const payload = { name: 'donorsearch', key: values.apiKey };
            return isEnabled
                ? updateIntegration(accountId, donorSearch.id, payload)
                : createIntegration(accountId, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.integrations(accountId),
            });
            success('Saved DonorSearch API key!');
        },
        onError: e => {
            error(
                e.response?.data?.message
                    ? `${e.response.data.message} Please check your API key.`
                    : 'Something went wrong. Please check your API key.',
            );
        },
    });

    return (
        <IntegrationItem.Root>
            <IntegrationItem.Header>
                <IntegrationItem.Logo>
                    <img
                        alt="DonorSearch"
                        className="max-w-[100px]"
                        src={`/images/logos/donorsearch.svg`}
                    />
                </IntegrationItem.Logo>

                <IntegrationItem.Description>
                    <div className="flex-center flex gap-4">
                        <p className="text-xl font-medium">DonorSearch</p>
                        <Tooltip.Root>
                            <Tooltip.Trigger>
                                <FontAwesomeIcon
                                    className="text-gray-600"
                                    icon={faQuestionCircle}
                                />
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content inverse>
                                    <p>
                                        Givebutter integrates with DonorSearch for free, but a
                                        subscription is required to use DonorSearch. Get started by
                                        visiting{' '}
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://help.givebutter.com/en/articles/5970284-donorsearch"
                                            className="text-white underline hover:text-white"
                                        >
                                            this guide.
                                        </a>
                                    </p>
                                    <Tooltip.Arrow inverse />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    </div>

                    <p className="max-w-xs text-base text-gray-700">
                        With DonorSearch's Wealth Screening tools, you'll be able to search for
                        donors based on their capacity and likelihood to give.
                    </p>

                    <a target="_blank" rel="noopener noreferrer" href="https://www.donorsearch.net">
                        https://www.donorsearch.net
                    </a>
                </IntegrationItem.Description>

                <IntegrationItem.Expander />
            </IntegrationItem.Header>
            <IntegrationItem.Content>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex flex-col items-start gap-4"
                    >
                        <Form.Container>
                            <Form.Label name={path('apiKey')} className="text-base">
                                API Key
                            </Form.Label>
                            <Form.TextInputRoot name={path('apiKey')} control={control}>
                                <Form.TextInputForm placeholder="Enter your API key" />
                            </Form.TextInputRoot>
                        </Form.Container>

                        <Form.SubmitButton isSubmitting={isLoading}>Save</Form.SubmitButton>
                    </form>
                </Form.Provider>
            </IntegrationItem.Content>
        </IntegrationItem.Root>
    );
};

export { DonorSearch };
